import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { mediaQueries } from 'shared/styles';

import { BackgroundField, BACKGROUND_AI_SCHEMA, DEFAULT_BACKGROUND } from '../fields';
import type { BackgroundValue } from '../fields';
import {
  BACKGROUND_OPACITY_FIELD,
  DEFAULT_BACKGROUND_OPACITY,
  backgroundStyleWithOpacity,
  backgroundValueColor,
  resolveBackgroundOpacityFields,
} from '../fields/background-opacity';
import { altTextField, resolveAltText } from '../fields/widgets/alt-text-field';
import type { AltTextValue } from '../fields/widgets/alt-text-field';
import { AssetPickerField } from '../fields/widgets/asset-picker-field';
import { selectField } from '../fields/widgets/select-field';
import { createSpacingFields } from '../fields/spacing-fields';
import { buildImgixDelivery, isImgixHostedSrc } from '../image-src';
import { IMAGE_ASPECT_RATIOS, lookup, MAX_WIDTHS, spacingToCss } from '../fields/tokens';
import type { BoxSpacing, ImageAspectRatio, MaxWidthOption } from '../fields/tokens';
import { imgixSizing } from './image-style';
import { isSafeSrc } from './image';
import type { SlotProps, StarterChild } from './slot';
import { starterChild } from './slot';

/**
 * An image beside authored content, stacking vertically on mobile.
 *
 * **Deliberately a thin block rather than a second FlexLayout.** `FlexLayout` can already express a
 * text-and-image split (direction Horizontal, itemSizing Equal width, mobileStack Stack
 * vertically, with text and an Image in its slot), so this block adds no new capability. It exists
 * because "assemble a flex row from sixteen controls" is a developer's answer, not an author's:
 * a labelled Split Banner is the one obvious way to do a common case, while FlexLayout stays
 * available for the general one.
 *
 * Its field set stays task-specific rather than mirroring FlexLayout's entire vocabulary: authors
 * choose the image's side and proportion, vertical alignment, and the shared appearance/spacing
 * controls needed by this common composition.
 *
 * **It stores no author text.** Content comes from a constrained slot allowing Heading, Text
 * (internal key `Paragraph`), Button and Image, exactly as `hero.tsx` does. That is load-bearing rather than stylistic: a block
 * holding its own author text would need its own sanitiser, hostile-input suite, and richtext
 * migration. Delegating means it already inherits Text's conversion and will inherit Heading's
 * separate follow-up without adding another text path.
 */

type ImagePosition = 'left' | 'right';
type ImageWidth = 'half' | 'one-third' | 'two-thirds';
type VerticalAlignment = 'bottom' | 'center' | 'top';

export type SplitBannerProps = {
  content?: React.FC<SlotProps>;
  imageSrc: string;
  imageAlt: AltTextValue;
  imagePosition: ImagePosition;
  imageWidth: ImageWidth;
  verticalAlign: VerticalAlignment;
  imageAspectRatio: ImageAspectRatio;
  background: BackgroundValue;
  backgroundOpacity: number;
  gap: string;
  maxWidth: MaxWidthOption;
  padding: BoxSpacing;
};

const IMAGE_POSITION_OPTIONS: { label: string; value: ImagePosition }[] = [
  { label: 'Left', value: 'left' },
  { label: 'Right', value: 'right' },
];

const IMAGE_WIDTH_OPTIONS: { label: string; value: ImageWidth }[] = [
  { label: 'Half', value: 'half' },
  { label: 'One third', value: 'one-third' },
  { label: 'Two thirds', value: 'two-thirds' },
];

const IMAGE_WIDTH_GROWTH = {
  half: { image: 1, content: 1 },
  'one-third': { image: 1, content: 2 },
  'two-thirds': { image: 2, content: 1 },
} as const;

const VERTICAL_ALIGNMENT_OPTIONS: { label: string; value: VerticalAlignment }[] = [
  { label: 'Center', value: 'center' },
  { label: 'Top', value: 'top' },
  { label: 'Bottom', value: 'bottom' },
];

const VERTICAL_ALIGNMENT = {
  center: 'center',
  top: 'flex-start',
  bottom: 'flex-end',
} as const;

const ASPECT_RATIO_OPTIONS: { label: string; value: ImageAspectRatio }[] = [
  { label: 'Original', value: 'original' },
  { label: 'Square (1:1)', value: 'square' },
  { label: 'Landscape (4:3)', value: 'landscape' },
  { label: 'Wide (16:9)', value: 'wide' },
  { label: 'Portrait (3:4)', value: 'portrait' },
];

const GAP_OPTIONS = [
  { label: 'None (0px)', value: '0px' },
  { label: 'XS (4px)', value: '4px' },
  { label: 'Small (8px)', value: '8px' },
  { label: 'Medium (16px)', value: '16px' },
  { label: 'Large (24px)', value: '24px' },
  { label: 'XL (32px)', value: '32px' },
  { label: '2XL (48px)', value: '48px' },
] as const;

const GAP_VALUES: ReadonlySet<string> = new Set(GAP_OPTIONS.map(({ value }) => value));

const MAX_WIDTH_OPTIONS: { label: string; value: MaxWidthOption }[] = [
  { label: 'Full', value: 'none' },
  { label: 'Normal', value: 'normal' },
  { label: 'Narrow', value: 'narrow' },
];

const DEFAULT_PADDING: BoxSpacing = { top: '32px', right: '32px', bottom: '32px', left: '32px' };

function resolveGap(value: unknown): string {
  return typeof value === 'string' && GAP_VALUES.has(value) ? value : '16px';
}

/**
 * Starter content, so a freshly dropped block reads as a split banner rather than an empty box with
 * an image on one side. Mirrors Hero's starter children; `id` is intentionally absent because
 * `populateIds()` stamps one and a hardcoded id would duplicate across instances.
 */
const SPLIT_BANNER_STARTER_CHILDREN: StarterChild[] = [
  starterChild('Heading', { text: '<p>A headline beside your image</p>', level: 'h2' }),
  starterChild('Paragraph', { text: '<p>Add a short description that supports the image.</p>' }),
];

/**
 * `row-reverse` rather than reordering the DOM: the image element stays FIRST in source order
 * regardless of which side it renders on, so a screen reader and a keyboard user meet the image and
 * the content in a stable order while the visual arrangement changes.
 *
 * Direction is a custom property read by the stylesheet rather than an inline style, because an
 * inline `flex-direction` cannot be overridden by a media query without `!important`. Same
 * mechanism `flex-layout.tsx` and `spacer.tsx` already use -- deliberately reused rather than
 * inventing a third.
 *
 * The mobile stack is not a separate control. A left/right split has to become a vertical stack on
 * a phone, and an author would not think to configure that; making it automatic is the whole point
 * of offering a preconfigured block.
 */
export const StyledSplitBanner = styled.div`
  display: flex;
  flex-direction: var(--cms-split-banner-direction, row);
  align-items: var(--cms-split-banner-vertical-align, center);
  box-sizing: border-box;
  width: 100%;
  gap: var(--cms-split-banner-gap, 16px);
  max-width: var(--cms-split-banner-max-width, none);
  margin-inline: auto;
  padding: var(--cms-split-banner-padding, 32px);

  & > [data-cms-split-banner-side='image'] {
    flex: var(--cms-split-banner-image-grow, 1) 1 0;
    min-width: 0;
  }

  & > :not([data-cms-split-banner-side='image']) {
    flex: var(--cms-split-banner-content-grow, 1) 1 0;
    min-width: 0;
  }

  @media ${mediaQueries.largePhone} {
    flex-direction: column;
    align-items: stretch;

    & > [data-cms-split-banner-side='image'],
    & > :not([data-cms-split-banner-side='image']) {
      flex: 0 0 auto;
      width: 100%;
    }
  }
`;

// No align-self override: the parent owns the selected top/center/bottom alignment, so this side
// sizes to its own content (the image's aspect-ratio-driven height) rather than stretching to match
// the content side's height. `align-self: stretch` here used to force a definite height on
// this box, which SplitImage's `height: 100%` then inherited -- making `aspect-ratio` a no-op per
// the CSS spec (it only affects sizing when at least one dimension is auto). Found in review of
// ECOMM-13237: selecting Square/Wide/Portrait/Landscape in the editor changed nothing visually.
const ImageSide = styled.div``;

// `height` is deliberately UNSET, not 100%, mirroring `image.tsx`'s StyledImage (the same field on
// a different block): only `width` is fixed, so `aspect-ratio` is free to compute `height` from it.
// Setting both dimensions makes aspect-ratio inert -- see the note on ImageSide above.
const SplitImage = styled.img`
  display: block;
  width: 100%;
  object-fit: cover;
  aspect-ratio: var(--cms-split-banner-aspect-ratio, auto);
`;

const ImagePlaceholderBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 160px;
  border: 1px dashed var(--dcm-cms-placeholder-border-color, #c2c9cd);
  border-radius: var(--dcm-cms-placeholder-radius, 8px);
  background: var(--dcm-cms-image-placeholder-bg, #f0f2f4);
  color: var(--dcm-cms-image-placeholder-color, #646d72);
  font-size: 14px;
`;

/** Saved page data is unvalidated JSON, so a stored src may be absent or a hostile scheme. */
function usableImageSrc(src: unknown): string {
  return typeof src === 'string' && src !== '' && isSafeSrc(src) ? src : '';
}

const SPACING_FIELDS = createSpacingFields();

export const splitBannerConfig: ComponentConfig<SplitBannerProps> = {
  label: 'Split Banner',
  ai: {
    instructions:
      'An image beside a short block of content, stacking vertically on mobile. Put the Heading and Text blocks in the content slot rather than describing the copy here. Use this for a single image-plus-copy pairing; use Flex Layout when a row needs more than two sides or custom alignment.',
  },
  fields: {
    content: { type: 'slot', allow: ['Heading', 'Paragraph', 'Button', 'Image'] },
    background: {
      type: 'custom',
      label: 'Background',
      render: BackgroundField,
      ai: {
        schema: BACKGROUND_AI_SCHEMA,
        instructions:
          'Background colour or image behind the whole banner. Prefer a theme colour role so it follows merchant branding.',
      },
    },
    backgroundOpacity: BACKGROUND_OPACITY_FIELD,
    imageSrc: {
      type: 'custom',
      label: 'Image',
      render: AssetPickerField,
      ai: {
        schema: { type: 'string' },
        stream: false,
        instructions: 'Full image URL. Do not stream partial URLs.',
      },
    },
    imageAlt: altTextField('Image alt text'),
    imageAspectRatio: selectField(
      'Image shape',
      ASPECT_RATIO_OPTIONS,
      'The frame the image is cropped to. Original keeps the uploaded proportions.'
    ),
    imagePosition: {
      type: 'radio',
      label: 'Image position',
      options: IMAGE_POSITION_OPTIONS,
      ai: {
        schema: { type: 'string', enum: IMAGE_POSITION_OPTIONS.map(({ value }) => value) },
        instructions: 'Which side the image sits on for desktop widths. On phones it always moves above the content.',
      },
    },
    imageWidth: selectField('Image width', IMAGE_WIDTH_OPTIONS, 'Image share of the banner at desktop widths.'),
    verticalAlign: selectField(
      'Vertical alignment',
      VERTICAL_ALIGNMENT_OPTIONS,
      'How the image and content align when their heights differ.'
    ),
    gap: selectField('Space between sides', GAP_OPTIONS, 'Gap between the image and the content.'),
    maxWidth: selectField('Banner width', MAX_WIDTH_OPTIONS, 'Constrains the banner width for readability.'),
    padding: {
      type: 'object',
      label: 'Padding',
      objectFields: SPACING_FIELDS,
    },
  },
  resolveFields: (data, { fields }) =>
    resolveBackgroundOpacityFields(backgroundValueColor(data.props.background), fields),
  defaultProps: {
    content: (SPLIT_BANNER_STARTER_CHILDREN as unknown) as React.FC<SlotProps>,
    imageSrc: '',
    imageAlt: { alt: '', decorative: false },
    imagePosition: 'left',
    imageWidth: 'half',
    verticalAlign: 'center',
    imageAspectRatio: 'landscape',
    background: DEFAULT_BACKGROUND,
    backgroundOpacity: DEFAULT_BACKGROUND_OPACITY,
    gap: '16px',
    maxWidth: 'none',
    padding: DEFAULT_PADDING,
  },
  render: ({
    content: slotContent,
    imageSrc = '',
    imageAlt,
    imagePosition = 'left',
    imageWidth = 'half',
    verticalAlign = 'center',
    imageAspectRatio = 'landscape',
    background,
    backgroundOpacity = DEFAULT_BACKGROUND_OPACITY,
    gap = '16px',
    maxWidth = 'none',
    padding = DEFAULT_PADDING,
    puck,
  }) => {
    const src = usableImageSrc(imageSrc);
    const alt = resolveAltText(imageAlt);
    const growth = lookup(IMAGE_WIDTH_GROWTH, imageWidth, IMAGE_WIDTH_GROWTH.half);
    // Keep fixed-density delivery (rather than claiming `100vw` for a partial-width slot), but size
    // the source for the authored proportion. One third remains within the 480px Medium target in
    // the common constrained canvas; Half and Two thirds use the 800px Large target so a wide,
    // unconstrained banner does not stretch a 480px source. Malformed/missing values follow Half.
    const deliveryPreset = imageWidth === 'one-third' ? 'medium' : 'large';
    const delivery = src && isImgixHostedSrc(src) ? buildImgixDelivery(src, imgixSizing(deliveryPreset)) : undefined;
    const Slot = slotContent;

    return (
      <StyledSplitBanner
        data-testid='cms-split-banner'
        data-image-position={imagePosition}
        style={
          {
            ...backgroundStyleWithOpacity(background, backgroundOpacity),
            // `row-reverse` keeps the image first in source order while moving it visually.
            '--cms-split-banner-direction': imagePosition === 'right' ? 'row-reverse' : 'row',
            '--cms-split-banner-image-grow': growth.image,
            '--cms-split-banner-content-grow': growth.content,
            '--cms-split-banner-vertical-align': lookup(VERTICAL_ALIGNMENT, verticalAlign, VERTICAL_ALIGNMENT.center),
            '--cms-split-banner-gap': resolveGap(gap),
            '--cms-split-banner-max-width': lookup(MAX_WIDTHS, maxWidth, MAX_WIDTHS.none),
            '--cms-split-banner-padding': spacingToCss(padding),
            '--cms-split-banner-aspect-ratio': lookup(
              IMAGE_ASPECT_RATIOS,
              imageAspectRatio,
              IMAGE_ASPECT_RATIOS.original
            ),
          } as React.CSSProperties
        }
      >
        {/*
         * The image side is omitted entirely when there is no image on a published page, rather than
         * rendered empty. Each direct side receives its authored flex proportion, so an empty image
         * side would still reserve its share of the row. An author who publishes before picking an
         * image would ship a conspicuous blank space beside their text. In the editor it stays, or the
         * author cannot tell the block has an image slot at all.
         */}
        {src || puck.isEditing ? (
          <ImageSide data-testid='cms-split-banner-image-side' data-cms-split-banner-side='image'>
            {src ? (
              <SplitImage
                src={delivery?.src ?? src}
                srcSet={delivery?.srcSet}
                sizes={delivery?.sizes}
                alt={alt.decorative ? '' : alt.alt}
                aria-hidden={alt.decorative ? true : undefined}
                loading='lazy'
              />
            ) : (
              <ImagePlaceholderBox>Pick an image in the settings panel.</ImagePlaceholderBox>
            )}
          </ImageSide>
        ) : null}
        {Slot ? <Slot /> : <div />}
      </StyledSplitBanner>
    );
  },
};
